import { AccountManager, Util } from '@bluedolphin/util-module-mf';
import React, { useEffect, useContext, useMemo } from 'react';
import { OFFICIAL_PROCESS } from '../../util/constants';
import { useAzureMsal } from '../azure';
import { useTenant } from '../tenant';
import { useUser } from '../user';
import useErrorModal, {
    ToggleErrorModalArgs,
    ModalErrorMessage,
} from '../../hooks/useErrorModal';
import { Application, getActiveApplications } from '../../util/misc';

const GUESTLINK_PATH = '/:tenantId/guestlink';
const OFFICIAL_PROCESS_PATH = '/:tenantId/drafts/officialprocess';
const PROJECTS_PATH = '/:tenantId/projects';
const PROCESSES_PATH = '/:tenantId/processes';

type ApplicationContextValue = {
    isPathActive: (_path: string) => boolean;
    isErrorModalVisible: boolean;
    errorModalMessage: ModalErrorMessage;
    toggleErrorModal: (_args: ToggleErrorModalArgs) => void;
};

const authURLs = [
    '/:tenantId/forcelogin',
    '/404',
    '/:tenantId/login',
    '/:tenantId/signup',
    '/:tenantId/settings/profile',
];

export const ApplicationContext =
    React.createContext<ApplicationContextValue>(null);

/**
 * Application provider
 */
export function ApplicationProvider({
    children,
    customProps: { applications: potentialApps, singleSpa },
}) {
    const { authenticationResult, getDeepLink } = useAzureMsal();
    const { tenant, setIsOfficialProcess, setTenantName, tenantMeta } =
        useTenant();
    const { user } = useUser();
    const { isVisible, toggleErrorModal, errorMessage } = useErrorModal();
    const applications = useMemo(
        () => getActiveApplications(tenant?.addons ?? [], potentialApps),
        [tenant?.addons?.length]
    );

    /**
     * if user no-authenticated then deep link url is updated
     * if guestLink then user goes forward
     */
    useEffect(() => {
        if (tenantMeta && authenticationResult?.state === 'no-authenticated') {
            if (isPathActive(GUESTLINK_PATH)) {
                registerAllApplications();
            } else if (isAnyAppPathActive()) {
                setIsOfficialProcess(isPathActive(OFFICIAL_PROCESS_PATH));
                AccountManager.setItem('deepLinkURL', getDeepLink());
                window.history.pushState({}, '', `/${tenantMeta.name}/login`);
            }
        }
    }, [tenantMeta, authenticationResult]);

    /**
     * If user then load apps and redirect to landing page
     */
    useEffect(() => {
        if (user?.id && tenant) {
            registerAllApplications();
        }
        if (user?.id) {
            redirectToLandingPage();
        }
    }, [user?.id, tenant]);

    /**
     * If user tries to load an unavailable route
     */
    useEffect(() => {
        if (user?.id && tenant?.name && applications?.length) {
            const availableURLs = getAllPaths(true).concat(authURLs);

            if (
                !availableURLs.some((value) =>
                    window.location.pathname
                        .replace(`/${tenant?.name}`, '')
                        .includes(value.replace('/:tenantId', ''))
                )
            ) {
                redirectToLandingPage(true);
            }
        }
    }, [user?.id, tenant?.name, applications?.length]);

    /**
     * If user has no roles and tries to go to projects, check old bpmn is active.
     * If it's active redirect to official processes page, otherwise to dashboard.
     */
    useEffect(() => {
        if (
            user?.roles.length === 0 &&
            tenant?.addons &&
            (isPathActive(PROJECTS_PATH) || isPathActive(PROCESSES_PATH))
        ) {
            const { rootURL } = AccountManager.getTenant();
            const isBpmnAddonActive = tenant?.addons.some(
                (addon) => addon.identifier === 'BPMN' && addon.is_available
            );
            const target = isBpmnAddonActive ? OFFICIAL_PROCESS : 'dashboard';
            if (!user?.is_guest_user) {
                window.history.pushState({}, '', `${rootURL}dashboard`);
            } else {
                window.history.pushState({}, '', `${rootURL}${target}`);
            }
        }
    }, [user, tenant?.addons]);

    /**
     * Registers an application in singleSpa engine
     */
    function registerAllApplications() {
        applications?.forEach(registerApplication);
    }

    /**
     * Registers applications in singleSpa and shares user and tenant models with all micro-apps
     *
     * @param {Record} app
     */
    function registerApplication(app: Record<string, any>) {
        if (!singleSpa.getAppStatus(app.name)) {
            app.customProps = {
                tenant,
                user,
                toggleErrorModal,
            };
            singleSpa.registerApplication(app);
        }
    }

    /**
     * Redirects the user to default landing page
     * only when is new login, user accesses from cache or
     * no route match
     */
    function redirectToLandingPage(forceRedirect = false) {
        const isRedirectFlow = authenticationResult.state !== 'authenticated';
        const activeApps = singleSpa.checkActivityFunctions();

        if (isRedirectFlow || activeApps.length === 1 || forceRedirect) {
            const { rootURL, landingPage, name } = AccountManager.getTenant();
            AccountManager.registerTenantName(name);
            setTenantName(name);
            Util.setGoTo(name);
            window.history.pushState({}, '', `${rootURL}${landingPage}`);
        }
    }

    /**
     * Returns the active path based on the href or undefined
     *
     * @param {Array} paths
     * @returns {string | undefined}
     */
    function getActivePath(paths: string[]): string | undefined {
        return paths.find((path: string) => isPathActive(path));
    }

    /**
     * Returns all paths
     *
     * @returns {Array}
     */
    function getAllPaths(excludeNavBar = false): string[] {
        return applications?.reduce((acc: string[], value: Application) => {
            if (excludeNavBar && value.name === '@bluedolphin/navbar-mf') {
                return acc;
            }
            return acc.concat(value.activeWhen);
        }, []);
    }

    /**
     * Returns true if any path is active or false, exluding login paths
     *
     * @returns {boolean}
     */
    function isAnyAppPathActive(): boolean {
        return !!getActivePath(getAllPaths());
    }

    /**
     * Returns true if the path is active or false
     *
     * @param {string} path
     * @returns {boolean}
     */
    function isPathActive(path: string): boolean {
        return !!singleSpa.pathToActiveWhen(path)(
            new URL(window.location.href)
        );
    }

    const contextValue = useMemo(
        () => ({
            isPathActive,
            isErrorModalVisible: isVisible,
            toggleErrorModal,
            errorModalMessage: errorMessage,
        }),
        [isPathActive, isVisible, toggleErrorModal, errorMessage]
    );

    return (
        <ApplicationContext.Provider value={contextValue}>
            {children}
        </ApplicationContext.Provider>
    );
}

/**
 * A wrapper around the useContext Hook
 * Returns Application context data
 *
 * @returns {TenantContextValue}
 */
export function useApplication(): ApplicationContextValue {
    return useContext(ApplicationContext);
}
